import React, { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "./firebase";
import "./LocationLabel.css";

/* -------------------------------------------------
   Hard-coded data (unchanged)
   ------------------------------------------------- */
const tooltips = [
  { name: "Internal Road", coords: "project6BaseCoords", className: "project-8-label", transform: "translate(-50%, -100%)", isBold: true },
  { name: "🛣️ Main Road", coords: "c1", className: "project-5-label" },
  { name: "🛣️ Main Road", coords: "c2", className: "project-5-label" },
  { name: "🏚️ Club House", coords: "c3", className: "i-label" },
  { name: "Phase 3", coords: "c6", className: "p-label" },
  { name: "Phase 2", coords: "c5", className: "p-label" }
];

const plots = [
  { name: "Plot H1",  x: 1161, y: 1179,  dataKey: "plot-h1" },
{ name: "Plot H2",  x: 1037, y: 1414,  dataKey: "plot-h2" },
{ name: "Plot H3",  x: 947,  y: 1570,  dataKey: "plot-h3" },
{ name: "Plot H4",  x: 988,  y: 1728,  dataKey: "plot-h4" },
{ name: "Plot H5",  x: 958,  y: 1898,  dataKey: "plot-h5" },
{ name: "Plot H6",  x: 930,  y: 2081,  dataKey: "plot-h6" },
{ name: "Plot H7",  x: 857,  y: 2251,  dataKey: "plot-h7" },
{ name: "Plot H9",  x: 1018, y: 2662,  dataKey: "plot-h9" },
{ name: "Plot H10", x: 1166, y: 2692,  dataKey: "plot-h10" },
{ name: "Plot H11", x: 1344, y: 2728,  dataKey: "plot-h11" },
{ name: "Plot H12", x: 1297, y: 2484,  dataKey: "plot-h12" },
{ name: "Plot H13", x: 1333, y: 2301,  dataKey: "plot-h13" },
{ name: "Plot H14", x: 1365, y: 2120,  dataKey: "plot-h14" },
{ name: "Plot H15", x: 1397, y: 1942,  dataKey: "plot-h15" },
{ name: "Plot H16", x: 1427, y: 1762,  dataKey: "plot-h16" },
{ name: "Plot H17", x: 1462, y: 1579,  dataKey: "plot-h17" },
{ name: "Plot H18", x: 1496, y: 1405,  dataKey: "plot-h18" },
{ name: "Plot H19", x: 1541, y: 1141,  dataKey: "plot-h19" },
];

/* Plot centers – now include a short label (H1, H2 …) */
const plotCenters = [
  { id: "plot-h1",  x: 1255, y: 1311,  label: "H1" },
{ id: "plot-h2",  x: 1205, y: 1484,  label: "H2" },
{ id: "plot-h3",  x: 1125, y: 1649,  label: "H3" },
{ id: "plot-h4",  x: 1162, y: 1811,  label: "H4" },
{ id: "plot-h5",  x: 1132, y: 1985,  label: "H5" },
{ id: "plot-h6",  x: 1095, y: 2167,  label: "H6" },
{ id: "plot-h7",  x: 1038, y: 2334,  label: "H7" },
{ id: "plot-h9",  x: 1115, y: 2846,  label: "H9" },
{ id: "plot-h10", x: 1271, y: 2849,  label: "H10" },
{ id: "plot-h11", x: 1450, y: 2844,  label: "H11" },
{ id: "plot-h12", x: 1437, y: 2606,  label: "H12" },
{ id: "plot-h13", x: 1482, y: 2401,  label: "H13" },
{ id: "plot-h14", x: 1526, y: 2213,  label: "H14" },
{ id: "plot-h15", x: 1565, y: 2030,  label: "H15" },
{ id: "plot-h16", x: 1600, y: 1851,  label: "H16" },
{ id: "plot-h17", x: 1632, y: 1669,  label: "H17" },
{ id: "plot-h18", x: 1671, y: 1490,  label: "H18" },
{ id: "plot-h19", x: 1713, y: 1264,  label: "H19" },
];

/* -------------------------------------------------
   Component
   ------------------------------------------------- */
const LocationLabels = ({
  labelStyle,
  getResponsivePosition,
  coords,
  onC5Click,
  onC7Click,
}) => {
  const [hoveredPlot, setHoveredPlot] = useState(null);
  const [plotStatuses, setPlotStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;

  /* ---- Firestore fetch ---- */
  useEffect(() => {
    const fetchPlotStatuses = async () => {
      try {
        const snap = await getDocs(collection(db, "vrukshaplot"));
        const map = snap.docs.reduce((acc, doc) => {
          acc[doc.id] = doc.data().Status || "Unknown";
          return acc;
        }, {});
        setPlotStatuses(map);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchPlotStatuses();
  }, []);

  /* ---- Hover handling (desktop only) ---- */
  useEffect(() => {
    if (isTouchDevice) return;

    const areas = document.querySelectorAll("area[data-key^='plot']");

    const over = (e) => {
      const key = e.target.getAttribute("data-key");
      const plot = plots.find((p) => p.dataKey === key);
      if (plot) setHoveredPlot(plot);
    };
    const out = () => setHoveredPlot(null);

    areas.forEach((a) => {
      a.addEventListener("mouseover", over);
      a.addEventListener("mouseout", out);
    });

    return () => {
      areas.forEach((a) => {
        a.removeEventListener("mouseover", over);
        a.removeEventListener("mouseout", out);
      });
    };
  }, [isTouchDevice]);

  if (loading) return null;

  return (
    <>
      {/* ==== STATIC TOOLTIPS ==== */}
      {tooltips.map((t, i) => {
        const c = coords[t.coords];
        if (!c) return null;

        return (
          <div
            key={`tt-${i}`}
            className={`persistent-tooltip ${t.className}`}
            onClick={(e) => {
              e.stopPropagation();
              if (t.coords === "c3" && onC5Click) onC5Click();
              if (t.coords === "c4" && onC7Click) onC7Click();
            }}
            style={{
              ...labelStyle,
              ...getResponsivePosition(c.x, c.y),
              transform: t.transform || "none",
              cursor: ["c5", "c7"].includes(t.coords) ? "pointer" : "default",
            }}
          >
            {t.isBold ? <strong>{t.name}</strong> : t.name}
          </div>
        );
      })}

      {/* ==== PLOT NUMBER + STATUS ==== */}
      {plotCenters.map((p) => {
        const status = plotStatuses[p.id] || "Unknown";
        const isAvailable = status === "Available";

        /* base styles for the label */
        const base = {
          ...labelStyle,
          ...getResponsivePosition(p.x, p.y),
          transform: "translate(-50%, -50%)",
          fontSize: "9px",
          color: "white",
          textAlign: "center",
          pointerEvents: "none",
        };

        /* colour logic */
        let bg = "transparent";
        let border = "1px solid rgba(255, 255, 255, 1)";
        if (!isAvailable) {
          if (status === "Sold") bg = "red";
          else if (status === "Booked") bg = "orange";
          else bg = "rgba(36,35,35,0.9)";
          border = "none";
        }

        return (
          <div
            key={`pc-${p.id}`}
            className="plot-center-label"
            style={{ ...base, backgroundColor: bg, border, padding: "2px 4px", borderRadius: "2px" }}
          >
            {/* Plot number – always visible */}
            <div style={{ fontWeight: "bold", fontSize: isAvailable ? "5px" : "6px" }}>
              {p.label}
            </div>

            {/* Status – only when not Available */}
            {!isAvailable && (
              <div style={{ fontSize: "6px", opacity: 0.9 }}>{status}</div>
            )}
          </div>
        );
      })}

      {/* ==== HOVER LABEL (desktop) – **unchanged style** ==== */}
      {!isTouchDevice && hoveredPlot && (
        <div
          className="plot-hover-label"
          style={{
            ...labelStyle,
            ...getResponsivePosition(hoveredPlot.x, hoveredPlot.y),
            transform: "translate(-50%, -50%)",
          }}
        >
          {hoveredPlot.name}
        </div>
      )}
    </>
  );
};

export default LocationLabels;